import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Block } from "../blockModel/types";
import { EmailBuilderActions } from "./useEmailBuilder";
import { RenderCtx, renderBlock } from "./blockRenderers";
import { UseBlockDragResult } from "./useBlockDrag";
import { writeBackFromEl } from "./writeBack";

// ---------------------------------------------------------------------------
// Styled helpers (inline — keep this file free of extra deps)
// ---------------------------------------------------------------------------

const S = {
	wrap: (selected: boolean): React.CSSProperties => ({
		position: "relative",
		transition: "background 0.12s",
		background: selected ? "#eaf3fb" : undefined,
		cursor: "default",
	}),
	handle: {
		position: "absolute" as const,
		left: -1,
		top: 8,
		width: 24,
		height: 26,
		borderRadius: 7,
		background: "#fff",
		border: "1px solid #e3e8ef",
		color: "#64748b",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "grab",
		zIndex: 6,
		boxShadow: "0 1px 3px rgba(15,23,42,.1)",
		fontSize: 12,
		userSelect: "none" as const,
	},
	del: {
		position: "absolute" as const,
		right: 8,
		top: 8,
		width: 26,
		height: 26,
		borderRadius: 7,
		background: "#fff",
		border: "1px solid #e3e8ef",
		color: "#dc2626",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		zIndex: 6,
		boxShadow: "0 1px 3px rgba(15,23,42,.1)",
		fontSize: 13,
	},
	dup: {
		position: "absolute" as const,
		right: 40,
		top: 8,
		width: 26,
		height: 26,
		borderRadius: 7,
		background: "#fff",
		border: "1px solid #e3e8ef",
		color: "#005e9e",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		zIndex: 6,
		boxShadow: "0 1px 3px rgba(15,23,42,.1)",
		fontSize: 12,
	},
	tag: {
		position: "absolute" as const,
		top: 0,
		left: 10,
		transform: "translateY(-50%)",
		background: "#005e9e",
		color: "#fff",
		fontSize: 10,
		fontWeight: 600,
		padding: "2px 8px",
		borderRadius: 6,
		zIndex: 5,
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		userSelect: "none" as const,
	},
	addRow: {
		margin: "10px 0 2px",
		padding: 9,
		border: "1.5px dashed #bcd0e6",
		borderRadius: 9,
		textAlign: "center" as const,
		fontSize: 12.5,
		fontWeight: 600,
		color: "#005e9e",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 6,
		background: "#fff",
	},
	dropLine: {
		height: 3,
		background: "#005e9e",
		borderRadius: 3,
		margin: "3px 12px",
	},
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Determine if this block type supports "+ Thêm dòng". */
function supportsRows(block: Block): boolean {
	return block.type === "summary" || block.type === "notes" || block.type === "footer";
}

/** Determine if this block type supports per-row drag-reorder. */
function supportsRowDrag(block: Block): boolean {
	return supportsRows(block) || block.type === "header";
}

/** Row-bound element fields ("blockId:FIELD:index"). */
// "line" covers header freeform lines (data-el = "blockId:line:N")
// "rraw" covers summary section-title rows (# Title) so selecting one surfaces the
// row drag handle (⠿) + del/dup, letting section titles reorder like any other row.
const ROW_EL_FIELDS = ["rraw", "rlbl", "rval", "rlbl2", "rval2", "nk", "nv", "nli", "fline", "line"];

/** Parse selEl → row index when it targets a row field of THIS block, else null. */
function rowIndexFromSelEl(selEl: string | null, blockId: string): number | null {
	if (!selEl) return null;
	const parts = selEl.split(":");
	if (parts.length < 3 || parts[0] !== blockId) return null;
	if (!ROW_EL_FIELDS.includes(parts[1])) return null;
	const idx = parseInt(parts[2], 10);
	return isNaN(idx) ? null : idx;
}

const rowFloatBtn: React.CSSProperties = {
	width: 24,
	height: 24,
	borderRadius: 6,
	background: "#fff",
	border: "1px solid #e3e8ef",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	cursor: "pointer",
	boxShadow: "0 1px 3px rgba(15,23,42,.12)",
};

/** Parse data-el="blockId:fieldBase[:index]" → { blockId, field, index } */
function parseDataEl(dataEl: string | null): { blockId: string; field: string; index: number } | null {
	if (!dataEl) return null;
	const parts = dataEl.split(":");
	// Minimum: blockId + field (2 parts); with index = 3 parts
	if (parts.length < 2) return null;
	const blockId = parts[0];
	const field = parts[1];
	const index = parts.length >= 3 ? parseInt(parts[2], 10) : -1;
	return { blockId, field, index };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BlockShellProps {
	block: Block;
	actions: EmailBuilderActions;
	ctx: RenderCtx;
	selId: string | null;
	selEl: string | null;
	drag: UseBlockDragResult;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BlockShell({ block, actions, ctx, selId, selEl, drag }: BlockShellProps): JSX.Element {
	const id = block.id!;
	const selected = selId === id;
	const wrapRef = useRef<HTMLDivElement>(null);

	// ---- Selection: mousedown on data-el sets element selection; on blank sets block selection ----
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			const target = e.target as HTMLElement;
			const elEl = target.closest("[data-el]") as HTMLElement | null;
			if (elEl) {
				const dataEl = elEl.getAttribute("data-el");
				actions.setSel(id);
				actions.setSelEl(dataEl);
				// don't prevent default — we want focus to go to the contentEditable
			} else {
				// Clicked on empty block area
				actions.setSel(id);
				actions.setSelEl(null);
				e.preventDefault();
			}
		},
		[id, actions]
	);

	// ---- Blur write-back (capture so it fires before parent gains focus) ----
	const handleBlur = useCallback(
		(e: React.FocusEvent) => {
			const target = e.target as HTMLElement;
			if (!target.hasAttribute("data-el")) return;
			if (!target.getAttribute("data-f")) return;
			writeBackFromEl(target, id, actions);
		},
		[id, actions]
	);

	// ---- Ctrl/Cmd+B → bold inside contentEditable ----
	// ---- Backspace/Delete → atomic chip removal guard ----
	//
	// Problem (Bug 1): When a `contentEditable=false` var-chip span is selected
	// (Chrome shows a blue highlight around the atomic chip), pressing Backspace or
	// Delete in some browser/OS combinations deletes the chip AND adjacent text nodes
	// rather than just the chip — wiping the entire field content.
	//
	// Fix: When the current selection is a Range that starts and ends at the same
	// parent node and spans exactly one child node that is a var-chip, intercept the
	// key, delete only that child node from the DOM, then call writeBackFromEl so
	// React state is updated from the corrected DOM.  All other Backspace/Delete
	// keystrokes are forwarded to the browser as normal.
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "b") {
				e.preventDefault();
				document.execCommand("bold");
				return;
			}

			if (e.key !== "Backspace" && e.key !== "Delete") return;

			const sel = window.getSelection();
			if (!sel || sel.type !== "Range" || !sel.rangeCount) return;

			const range = sel.getRangeAt(0);
			// Only intercept when the selection container is a contentEditable we own.
			const editable = (range.commonAncestorContainer as HTMLElement).closest
				? ((range.commonAncestorContainer as HTMLElement).closest("[data-el]") as HTMLElement | null)
				: null;
			if (!editable) return;

			// Determine whether the selection covers exactly one node and that node is a chip.
			const parent = range.commonAncestorContainer;
			const startNode = range.startContainer;
			const endNode = range.endContainer;

			// Case: entire range is within the same parent element, spanning one child chip.
			if (parent === startNode && parent === endNode && range.endOffset - range.startOffset === 1) {
				const chipNode = (parent as Element).childNodes[range.startOffset] as Element | null;
				if (chipNode && (chipNode as Element).getAttribute && (chipNode as Element).getAttribute("data-var")) {
					// This is exactly a chip node selected — remove it surgically.
					e.preventDefault();
					chipNode.parentNode?.removeChild(chipNode);
					sel.removeAllRanges();
					// Write back the corrected DOM to React state.
					const blockId = editable.getAttribute("data-el")?.split(":")[0] ?? null;
					if (blockId) writeBackFromEl(editable, blockId, actions);
				}
			}
		},
		[actions]
	);

	// ---- Block controls ----
	const handleDel = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			actions.delBlock(id);
		},
		[id, actions]
	);

	const handleDup = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			actions.dupBlock(id);
		},
		[id, actions]
	);

	const handleAddRow = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			actions.addRow(id);
		},
		[id, actions]
	);

	// Header-line add/delete — clicks on .e-hdrline-add / .e-hdrline-del buttons
	// rendered inside renderHeader are caught here via event delegation.
	const handleHdrLineClick = useCallback(
		(e: React.MouseEvent) => {
			const addBtn = (e.target as HTMLElement).closest("[data-hdrline-add]") as HTMLElement | null;
			if (addBtn) {
				e.stopPropagation();
				const blockId = addBtn.getAttribute("data-block-id") || id;
				actions.addHdrLine(blockId);
				return;
			}
			const delBtn = (e.target as HTMLElement).closest("[data-hdrline-del]") as HTMLElement | null;
			if (delBtn) {
				e.stopPropagation();
				const blockId = delBtn.getAttribute("data-block-id") || id;
				const idx = parseInt(delBtn.getAttribute("data-hdrline-del") || "-1", 10);
				if (idx >= 0) actions.delHdrLine(blockId, idx);
				return;
			}
		},
		[id, actions]
	);

	const handleDragHandle = useCallback(
		(e: React.PointerEvent) => {
			drag.onHandlePointerDown(id, e);
		},
		[id, drag]
	);

	const showDropLine = drag.dropLineBeforeId === id;
	// Structural header/footer can't be duplicated/deleted (not re-addable from palette).
	const canModify = block.type !== "header" && block.type !== "footer";

	// ---- Inline row controls: float del/dup buttons next to the selected row ----
	const selRowIndex = rowIndexFromSelEl(selEl, id);
	const [rowBtnTop, setRowBtnTop] = useState<number | null>(null);
	useLayoutEffect(() => {
		if (selRowIndex == null || !wrapRef.current || !selEl) {
			setRowBtnTop(null);
			return;
		}
		const el = wrapRef.current.querySelector(`[data-el="${selEl}"]`) as HTMLElement | null;
		if (!el) {
			setRowBtnTop(null);
			return;
		}
		const wrapRect = wrapRef.current.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		setRowBtnTop(elRect.top - wrapRect.top + Math.max(0, (elRect.height - 24) / 2));
	}, [selEl, selRowIndex, block]);

	const handleRowDel = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (selRowIndex == null) return;
			actions.delRow(id, selRowIndex);
			actions.setSelEl(null);
		},
		[id, selRowIndex, actions]
	);

	const handleRowDup = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (selRowIndex == null) return;
			actions.dupRow(id, selRowIndex);
		},
		[id, selRowIndex, actions]
	);

	// ---- Row drag-reorder (pointer-based, scoped to this block's [data-row] els) ----
	const [rowDropTop, setRowDropTop] = useState<number | null>(null);
	const rowDragRef = useRef<{
		from: number;
		startX: number;
		startY: number;
		moved: boolean;
		before: number | null;
	} | null>(null);

	const getRowEls = useCallback((): HTMLElement[] => {
		if (!wrapRef.current) return [];
		return Array.from(wrapRef.current.querySelectorAll(`[data-row^="${id}:"]`)) as HTMLElement[];
	}, [id]);

	const onRowDragMove = useCallback(
		(e: PointerEvent) => {
			const d = rowDragRef.current;
			if (!d || !wrapRef.current) return;
			if (!d.moved) {
				if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < 5) return;
				d.moved = true;
				document.body.style.userSelect = "none";
			}
			const els = getRowEls();
			const wrapRect = wrapRef.current.getBoundingClientRect();
			let before: number | null = null;
			let top: number | null = null;
			for (const el of els) {
				const idx = parseInt((el.getAttribute("data-row") || "").split(":")[1], 10);
				const r = el.getBoundingClientRect();
				if (e.clientY < r.top + r.height / 2) {
					before = idx;
					top = r.top - wrapRect.top;
					break;
				}
			}
			if (before === null && els.length > 0) {
				top = els[els.length - 1].getBoundingClientRect().bottom - wrapRect.top;
			}
			d.before = before;
			setRowDropTop(top);
		},
		[getRowEls]
	);

	const onRowDragUp = useCallback(() => {
		document.removeEventListener("pointermove", onRowDragMove);
		document.body.style.userSelect = "";
		const d = rowDragRef.current;
		rowDragRef.current = null;
		setRowDropTop(null);
		if (!d || !d.moved) return;
		const count = getRowEls().length;
		const to = d.before === null ? count - 1 : d.before > d.from ? d.before - 1 : d.before;
		if (to !== d.from) {
			actions.moveRow(id, d.from, to);
			// Indices shifted — the old selEl now points at a different row.
			actions.setSelEl(null);
		}
	}, [onRowDragMove, getRowEls, actions, id]);

	const handleRowDragStart = useCallback(
		(e: React.PointerEvent) => {
			if (e.button !== 0 || selRowIndex == null) return;
			e.preventDefault();
			e.stopPropagation();
			rowDragRef.current = {
				from: selRowIndex,
				startX: e.clientX,
				startY: e.clientY,
				moved: false,
				before: null,
			};
			document.addEventListener("pointermove", onRowDragMove);
			document.addEventListener("pointerup", onRowDragUp, { once: true });
		},
		[selRowIndex, onRowDragMove, onRowDragUp]
	);

	return (
		<>
			{showDropLine && <div style={S.dropLine} />}
			<div
				ref={wrapRef}
				className={"blk" + (selected ? " sel" : "")}
				data-id={id}
				style={S.wrap(selected)}
				onMouseDown={handleMouseDown}
				onClick={handleHdrLineClick}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}>
				{/* Block type tag (shown when selected) */}
				{selected && (
					<div style={S.tag}>
						{block.type}
						{block.required && <span style={{ opacity: 0.85, fontWeight: 500 }}>• bắt buộc</span>}
					</div>
				)}

				{/* Drag handle */}
				<div
					style={S.handle}
					onPointerDown={handleDragHandle}
					title="Kéo để sắp xếp">
					⠿
				</div>

				{/* Dup + Del cả khối — ẨN khi đang chọn một dòng bên trong: hai cặp nút
				    xoá (khối vs dòng) hiện cùng lúc từng làm user xoá nhầm cả khối. */}
				{selected && canModify && selRowIndex == null && (
					<>
						<div
							style={S.dup}
							onClick={handleDup}
							title="Nhân đôi cả khối">
							<i
								className="material-icons"
								style={{ fontSize: 16 }}>
								content_copy
							</i>
						</div>
						<div
							style={S.del}
							onClick={handleDel}
							title="Xóa cả khối">
							<i
								className="material-icons"
								style={{ fontSize: 16 }}>
								delete
							</i>
						</div>
					</>
				)}

				{/* Row drag handle — float on the LEFT of the selected row */}
				{rowBtnTop != null && selRowIndex != null && supportsRowDrag(block) && (
					<div
						style={{ position: "absolute", top: rowBtnTop, left: 8, zIndex: 7 }}
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}>
						<div
							style={{ ...rowFloatBtn, cursor: "grab", color: "#64748b", fontSize: 12 }}
							onPointerDown={handleRowDragStart}
							title="Kéo để sắp xếp dòng"
							data-testid="row-float-drag">
							⠿
						</div>
					</div>
				)}

				{/* Drop indicator while dragging a row */}
				{rowDropTop != null && (
					<div
						style={{
							position: "absolute",
							left: 20,
							right: 20,
							top: rowDropTop - 1,
							height: 3,
							background: "#005e9e",
							borderRadius: 3,
							zIndex: 8,
							pointerEvents: "none",
						}}
					/>
				)}

				{/* Inline row controls — float next to the selected row (del/dup that line).
				    Header lines use their own per-line × button; skip dup/del panel for header. */}
				{rowBtnTop != null && selRowIndex != null && supportsRows(block) && (
					<div
						style={{
							position: "absolute",
							top: rowBtnTop,
							right: 8,
							display: "flex",
							gap: 4,
							zIndex: 7,
						}}
						// Stop the wrapper's mousedown (it would clear selEl) and keep field focus.
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}>
						<div
							style={{ ...rowFloatBtn, color: "#005e9e" }}
							onClick={handleRowDup}
							title="Nhân đôi dòng"
							data-testid="row-float-dup">
							<i
								className="material-icons"
								style={{ fontSize: 14 }}>
								content_copy
							</i>
						</div>
						<div
							style={{ ...rowFloatBtn, color: "#dc2626", borderColor: "#fecaca" }}
							onClick={handleRowDel}
							title="Xóa dòng"
							data-testid="row-float-del">
							<i
								className="material-icons"
								style={{ fontSize: 14 }}>
								delete
							</i>
						</div>
					</div>
				)}

				{/* Block content — editable */}
				{renderBlock(block, { ...ctx, editable: true })}

				{/* "+ Thêm dòng" for blocks that support rows. Section titles ("Tiêu đề mục")
				    are added by dragging the palette item into the summary instead. */}
				{selected && supportsRows(block) && (
					<div
						style={S.addRow}
						onClick={handleAddRow}>
						+ Thêm dòng
					</div>
				)}
			</div>
		</>
	);
}
